/**
 * Avisos de la sección que valen para TODAS las pantallas: marca frenada, marca de demostración,
 * servidor sin URL pública, conexión con Twilio con problemas, el saldo del proveedor que no alcanza
 * para lo que cargaron las marcas (sólo al equipo) y error al traer la cuenta. Se muestran todos los
 * que apliquen, en este orden.
 *
 * Lo simulado se marca siempre (un número simulado que parece real es el error más caro de esta sección),
 * pero desde el 07/10/2026 no es un banner: es `ChipModoSimulado`, en la fila de arriba de cada pantalla. El
 * banner ocupaba 46 px en la computadora y 210 en el celular en TODAS las pantallas, antes del contenido.
 *
 * «La conexión con Twilio tiene problemas» sale de la alerta `diagnostico_con_errores` del Resumen
 * (el backend la arma con el último diagnóstico guardado, sin llamar a Twilio), y el saldo que no
 * alcanza, de `cobertura_twilio` (con el último saldo de Twilio que se leyó). Se pide una vez al
 * entrar y cada vez que la cuenta se recarga (después de conectar o de arreglar algo). Si ese
 * pedido falla, no se muestra nada: el diagnóstico completo está en Configuración → Cuenta.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, OctagonAlert, Info, AlertTriangle, PlugZap, Wallet } from "lucide-react";
import type { ReactNode } from "react";
import type { AlertaResumen, Resumen } from "../types";
import { oficialApi } from "../api";
import { useCuentaOficial } from "../contexto";
import { destinoAlerta } from "../logic/alertas";
import { leerRespuesta } from "../logic/respuesta";
import { textoAlertaTwilio } from "../logic/textos";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { TEXTO_DONDE_CAMBIAR_COMERCIO, TEXTO_MARCA_DEMO, textoMarcaFrenada } from "../logic/conexion";

interface AlertasDelBanner { diagnostico: AlertaResumen | null; cobertura: AlertaResumen | null }
const NINGUNA: AlertasDelBanner = { diagnostico: null, cobertura: null };

/**
 * Las alertas del Resumen que van en un banner: `diagnostico_con_errores` y `cobertura_twilio`. Sólo
 * con cuenta real (id): una cuenta virtual no tiene diagnóstico ni cuenta del proveedor. `cuenta` es
 * la dependencia a propósito: `recargar()` del contexto la reemplaza después de conectar o de
 * arreglar algo, y ahí se vuelve a preguntar.
 */
function useAlertasDelBanner(cuenta: { id: string | null } | null): AlertasDelBanner {
  const [alertas, setAlertas] = useState<AlertasDelBanner>(NINGUNA);
  useEffect(() => {
    if (!cuenta || cuenta.id === null) { setAlertas(NINGUNA); return; }
    let vivo = true;
    (async () => {
      try {
        const l = leerRespuesta<Resumen>(await oficialApi.resumen());
        if (!vivo) return;
        const lista = l.ok && Array.isArray(l.data?.alertas) ? l.data.alertas : [];
        const de = (codigo: string) => lista.find((x) => x?.codigo === codigo) ?? null;
        setAlertas({ diagnostico: de("diagnostico_con_errores"), cobertura: de("cobertura_twilio") });
      } catch {
        if (vivo) setAlertas(NINGUNA);
      }
    })();
    return () => { vivo = false; };
  }, [cuenta]);
  return alertas;
}

function Banner({ tono, icono, children }: { tono: "ambar" | "rojo" | "gris"; icono: ReactNode; children: ReactNode }) {
  const cls = tono === "ambar" ? "border-amber-200 bg-amber-50 text-amber-800"
    : tono === "rojo" ? "border-red-200 bg-red-50 text-red-700"
    : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div role={tono === "rojo" ? "alert" : "status"} className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm ${cls}`}>
      <span className="flex-shrink-0 mt-0.5">{icono}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function BannersOficial() {
  const { modo, cuenta, error } = useCuentaOficial();
  // La conexión con Twilio la ve y la arregla el equipo: a un cliente no se le manda a Configuración.
  const equipo = useEsEquipoDeenex();
  const { diagnostico: alertaDiagnostico, cobertura: alertaCobertura } = useAlertasDelBanner(cuenta);
  const banners: ReactNode[] = [];

  const frenada = textoMarcaFrenada(cuenta, equipo);
  if (frenada) {
    // El motivo del backend (con «Trust Hub», «portfolio pacing» o el código) queda para la tarjeta técnica de
    // Conexión; acá, dicho para quien lo lee (`textoMarcaFrenada`). A un cliente, sin motivo y sin ir a Configuración.
    banners.push(
      <Banner key="frenada" tono="rojo" icono={<OctagonAlert size={16} />}>
        <strong>{frenada.titulo}.</strong> {frenada.detalle}{" "}
        {equipo && <Link to="/activa/oficial/configuracion?seccion=cuenta" className="font-semibold underline">Ver en Configuración</Link>}
      </Banner>,
    );
  }
  if (modo === "twilio" && cuenta?.marcaDemoBloqueada) {
    banners.push(
      // Dice qué hacer y dónde: «acá no se cargan credenciales reales» no decía que el camino era elegir el
      // comercio del cliente, en el selector de marca del menú.
      <Banner key="demo" tono="gris" icono={<Info size={16} />}>
        {TEXTO_MARCA_DEMO} {TEXTO_DONDE_CAMBIAR_COMERCIO}
      </Banner>,
    );
  }
  if (modo === "twilio" && cuenta && !cuenta.publicBaseUrlConfigurada) {
    banners.push(
      <Banner key="url" tono="rojo" icono={<AlertTriangle size={16} />}>
        El servidor no tiene URL pública (PUBLIC_BASE_URL): sin eso no llegan recibos ni bajas y el envío está bloqueado.
      </Banner>,
    );
  }
  if (alertaDiagnostico) {
    const texto = String(alertaDiagnostico.mensaje || "").trim() || textoAlertaTwilio("diagnostico_con_errores");
    banners.push(
      <Banner key="diagnostico" tono="ambar" icono={<PlugZap size={16} />}>
        {texto}{" "}
        {equipo && <Link to="/activa/oficial/configuracion?seccion=cuenta" className="font-semibold underline">Ver el diagnóstico</Link>}
      </Banner>,
    );
  }
  // El saldo del proveedor no alcanza para lo que cargaron las marcas: lo resuelve el equipo (cargando la cuenta del
  // proveedor; una recarga en la plataforma no lo carga) y con subcuentas la suma trae la plata de otros comercios. A una
  // persona de un cliente no se le muestra aunque un backend anterior se la mande. Rojo en el mínimo de la alerta de saldo.
  if (equipo && alertaCobertura) {
    const texto = String(alertaCobertura.mensaje || "").trim() || textoAlertaTwilio("cobertura_twilio");
    const destino = destinoAlerta(alertaCobertura);
    banners.push(
      <Banner key="cobertura" tono={alertaCobertura.nivel === "corte" ? "rojo" : "ambar"} icono={<Wallet size={16} />}>
        {texto}{" "}
        <Link to={destino.ruta} className="font-semibold underline">{destino.label}</Link>
      </Banner>,
    );
  }
  if (error) {
    banners.push(
      <Banner key="error" tono="rojo" icono={<AlertTriangle size={16} />}>
        No pudimos traer la cuenta de WhatsApp oficial. {error}
      </Banner>,
    );
  }

  if (!banners.length) return null;
  return <div className="space-y-2">{banners}</div>;
}

/**
 * La marca de modo simulado, en la fila de arriba de cada pantalla (`OficialLayout`). Corta a la vista y
 * entera para el mouse (`title`) y el lector de pantalla.
 */
export function ChipModoSimulado() {
  const { modo } = useCuentaOficial();
  if (modo !== "simulated") return null;
  const completo = "Nada sale a WhatsApp: las aprobaciones, los recibos y las respuestas son de prueba y no cuentan en la medición ni en el costo real.";
  return (
    <span role="status" title={completo} className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
      <FlaskConical size={13} aria-hidden="true" />
      <span><strong>Modo simulado</strong> · nada sale a WhatsApp</span>
      <span className="sr-only">. {completo}</span>
    </span>
  );
}

export default BannersOficial;
